import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { HeartPulse, Mail, Phone, MapPin, ArrowLeft, CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";

const SUBJECTS = [
  "Devenir hôpital partenaire pilote",
  "Demande de démonstration",
  "Investisseur / Bailleur",
  "Presse / Journaliste",
  "ONG ou institution",
  "Autre",
];

export default function Contact() {
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", organization: "",
    subject: SUBJECTS[0], message: "",
    commune: "", quartier: "", avenue: "", ville: "", pays: "",
  });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'envoi");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#1A1D1A]">
      <header className="bg-white border-b border-[#E5EBE7]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="contact-logo">
            <div className="w-9 h-9 rounded-xl bg-[#1E4631] grid place-items-center">
              <HeartPulse className="w-5 h-5 text-white"/>
            </div>
            <span className="font-[Outfit] font-bold text-lg">KWIKILA Care</span>
          </Link>
          <Link to="/" className="text-sm text-[#5C6B61] hover:text-[#1E4631] inline-flex items-center gap-1.5" data-testid="contact-back">
            <ArrowLeft className="w-4 h-4"/> Accueil
          </Link>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-16 grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2 space-y-8">
          <div>
            <h1 className="text-4xl md:text-5xl font-[Outfit] font-bold leading-tight">Contactez l'équipe KWIKILA Care</h1>
            <p className="mt-4 text-[#5C6B61] text-lg">Une question, un partenariat, une demande de démo ? Écrivez-nous, nous répondons sous 48h.</p>
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-3 p-4 rounded-xl bg-white border border-[#E5EBE7]">
              <Mail className="w-5 h-5 text-[#D96C4A]"/>
              <a href="mailto:contact@kwikilacare.com" className="hover:underline">contact@kwikilacare.com</a>
            </div>
            <div className="flex items-center gap-3 p-4 rounded-xl bg-white border border-[#E5EBE7]">
              <Phone className="w-5 h-5 text-[#D96C4A]"/>
              <span>+243 824 314 459</span>
            </div>
            <div className="flex items-center gap-3 p-4 rounded-xl bg-white border border-[#E5EBE7]">
              <MapPin className="w-5 h-5 text-[#D96C4A]"/>
              <span>Kinshasa, République Démocratique du Congo</span>
            </div>
          </div>
          <div className="p-5 rounded-xl bg-[#1E4631]/5 border border-[#1E4631]/10 text-sm text-[#5C6B61]">
            <strong className="text-[#1E4631] block mb-1">La santé en confiance.</strong>
            KWIKILA Care numérise l'accueil hospitalier en RDC — femmes enceintes, enfants 0-3 ans, personnes âgées — en 5 langues nationales.
          </div>
        </div>

        <div className="lg:col-span-3">
          {done ? (
            <div className="bg-white rounded-2xl border border-[#E5EBE7] p-10 text-center" data-testid="contact-success">
              <div className="w-16 h-16 rounded-full bg-[#2E7D32]/10 grid place-items-center mx-auto mb-5">
                <CheckCircle2 className="w-8 h-8 text-[#2E7D32]"/>
              </div>
              <h2 className="text-2xl font-[Outfit] font-bold mb-3">Message envoyé !</h2>
              <p className="text-[#5C6B61] mb-6">Merci pour votre intérêt. L'équipe KWIKILA Care vous répondra sous 48h.</p>
              <Button onClick={() => { setDone(false); setForm({ name: "", email: "", phone: "", organization: "", subject: SUBJECTS[0], message: "", commune: "", quartier: "", avenue: "", ville: "", pays: "" }); }} variant="outline" data-testid="contact-new">
                Envoyer un autre message
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white rounded-2xl border border-[#E5EBE7] p-8 space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Nom complet *</Label><Input required value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="contact-name"/></div>
                <div><Label>Email *</Label><Input required type="email" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} data-testid="contact-email"/></div>
                <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} placeholder="+243..." data-testid="contact-phone"/></div>
                <div><Label>Organisation</Label><Input value={form.organization} onChange={(e)=>setForm({...form, organization: e.target.value})} placeholder="Hôpital, ONG, fondation..." data-testid="contact-org"/></div>
              </div>
              <div>
                <Label>Sujet *</Label>
                <Select value={form.subject} onValueChange={(v)=>setForm({...form, subject: v})}>
                  <SelectTrigger data-testid="contact-subject"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {SUBJECTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Message *</Label>
                <Textarea required rows={6} value={form.message} onChange={(e)=>setForm({...form, message: e.target.value})} placeholder="Présentez-vous brièvement et précisez votre demande..." data-testid="contact-message"/>
              </div>
              <div className="border-t border-[#E5EBE7] pt-4">
                <div className="text-xs font-semibold uppercase text-[#5C6B61] mb-2">Adresse physique (facultatif)</div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label>Commune</Label><Input value={form.commune} onChange={(e)=>setForm({...form, commune: e.target.value})} placeholder="Ex: Gombe" data-testid="contact-commune"/></div>
                  <div><Label>Quartier</Label><Input value={form.quartier} onChange={(e)=>setForm({...form, quartier: e.target.value})} placeholder="Ex: Q.7 Lemba" data-testid="contact-quartier"/></div>
                  <div className="sm:col-span-2"><Label>Avenue et N°</Label><Input value={form.avenue} onChange={(e)=>setForm({...form, avenue: e.target.value})} placeholder="Ex: Av. Kasa-Vubu N°123" data-testid="contact-avenue"/></div>
                  <div><Label>Ville</Label><Input value={form.ville} onChange={(e)=>setForm({...form, ville: e.target.value})} placeholder="Ex: Kinshasa" data-testid="contact-ville"/></div>
                  <div><Label>Pays</Label><Input value={form.pays} onChange={(e)=>setForm({...form, pays: e.target.value})} placeholder="Ex: RDC" data-testid="contact-pays"/></div>
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-[#D96C4A] hover:bg-[#B85A3D] text-white py-6 font-semibold" data-testid="contact-submit">
                {loading ? "Envoi..." : <><Send className="w-4 h-4 mr-2"/> Envoyer le message</>}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
